// src/components/membership/AcceptedAppsPickerModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, X, CheckCircle2, Loader2,
  Building2, MapPin, IdCard, CalendarDays, Mail, CheckCircle
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
  searchAcceptedMembershipApps,
  getMembershipApplication,
} from '../../services/membershipApplications';
import { Banner } from '../common/Banner';

// ⚠️ Ajusta esta ruta si tu MembershipDetailsModal está en otro lugar:
import DetailsModal from '../../pages/Menu/Vinculation/panel/MembershipDetailsModal';

/* ---------- Utils ---------- */
const get = (obj, path, def = '') => {
  try {
    const out = path.split('.').reduce((a, k) => (a && a[k] != null ? a[k] : undefined), obj);
    return out ?? def;
  } catch { return def; }
};

const normalizeRut = (v) => {
  if (!v) return '';
  const d = String(v).replace(/\D/g, '');
  if (d.length <= 1) return d;
  return `${d.slice(0, -1)}-${d.slice(-1)}`;
};

// Password = primeros 5 del cuerpo del RUT (sin DV)
const passwordFromRut = (rut) => {
  const digits = String(rut || '').replace(/\D/g, '');
  if (!digits) return '';
  const body = digits.slice(0, -1) || digits;
  return body.slice(0, 5);
};

const initials = (name) => {
  if (!name) return '–';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p.charAt(0).toUpperCase()).join('') || '–';
};

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d)) return '';
  return new Intl.DateTimeFormat('es-CL', { dateStyle: 'medium' }).format(d);
};

// Formatea la fecha de hoy en formato "dd-mm-yyyy"
const formatTodayDate = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}-${month}-${year}`;
};

// Función helper para buscar teléfono fijo recursivamente en el objeto
const findTelefonoFijo = (obj, depth = 0) => {
  if (depth > 3 || !obj || typeof obj !== 'object') return null;
  
  // Buscar campos que contengan "fijo" en el nombre
  const keys = Object.keys(obj);
  for (const key of keys) {
    const lowerKey = key.toLowerCase();
    // Buscar campos que contengan "fijo" pero no "movil"
    if (lowerKey.includes('fijo') && !lowerKey.includes('movil') && obj[key]) {
      return obj[key];
    }
    // Buscar recursivamente en objetos anidados (excepto arrays)
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      const found = findTelefonoFijo(obj[key], depth + 1);
      if (found) return found;
    }
  }
  return null;
};

/* === Mapeo fijo de rango UF -> valor del select (Micro|Small|Medium|Big) === */
const UF_TO_SIZE = {
  '0-2.400 uf anuales': 'Micro',
  '2.400,01 uf - 25.000 uf': 'Small',
  '25.000,01 uf - 100.000 uf': 'Medium',
  '100.000 uf y más': 'Big',
  '100.000 uf y mas': 'Big',
};

// Por si el backend ya entrega palabras
const WORD_TO_SIZE = {
  'micro': 'Micro',
  'microempresa': 'Micro',
  'pequeña': 'Small',
  'pequena': 'Small',
  'mediana': 'Medium',
  'grande': 'Big',
  'gran empresa': 'Big',
};

// Normaliza string (minúsculas, espacios simples)
const norm = (s = '') => String(s).toLowerCase().replace(/\s+/g, ' ').trim();

/** Resuelve el tamaño usando los valores fijos del formulario externo */
const resolveCompanySize = (app) => {
  const a = app?.answers || {};
  const empresa = a.empresa || {};

  // Candidatos donde puede venir el dato (ajusta/añade si tienes más)
  const candidates = [
    app?.tamanoEmpresa, a?.tamanoEmpresa, a?.tamano, empresa?.tamano, empresa?.tamaño,
    a?.rangoVentasUF, empresa?.rangoVentasUF, // "0-2.400 UF Anuales", etc.
  ].filter(Boolean);

  for (const raw of candidates) {
    const key = norm(raw);

    // 1) Coincidencia exacta por catálogo del formulario externo
    if (UF_TO_SIZE[key]) return UF_TO_SIZE[key];

    // 2) Coincidencia por palabra directa
    if (WORD_TO_SIZE[key]) return WORD_TO_SIZE[key];

    // 3) Pequeñas tolerancias por patrón (sin llenar de ifs)
    const HINTS = [
      [/0\s*[-–]\s*2\.?400/i, 'Micro'],
      [/2\.?400[,\.]?0?1.*25\.?000/i, 'Small'],
      [/25\.?000[,\.]?0?1.*100\.?000/i, 'Medium'],
      [/100\.?000.*(y\s*m[aá]s|o\s*m[aá]s|mas)/i, 'Big'],
    ];
    for (const [re, val] of HINTS) {
      if (re.test(String(raw))) return val;
    }
  }
  return '';
};

/** Calcula el valor de membresía en UF según el tamaño de la empresa
 * Micro = 2 UF, Small = 2.5 UF, Medium = 5 UF, Big = 10 UF
 */
const calculateMembershipValue = (companySize) => {
  if (!companySize) return '';
  
  const size = String(companySize).trim();
  const sizeUpper = size.toUpperCase();
  
  switch (sizeUpper) {
    case 'MICRO':
      return '2';
    case 'SMALL':
    case 'PEQUEÑA':
    case 'PEQUENA':
      return '2.5';
    case 'MEDIUM':
    case 'MEDIANA':
      return '5';
    case 'BIG':
    case 'LARGE':
    case 'GRANDE':
      return '10';
    default:
      return '';
  }
};

/** A partir de la postulación construimos los values del Formik */
const mapAppToNewMember = (app) => {
  // Parsear answers si es un string JSON
  let answersParsed = app?.answers;
  if (typeof answersParsed === 'string') {
    try {
      answersParsed = JSON.parse(answersParsed);
    } catch (e) {
      console.warn('No se pudo parsear answers como JSON:', e);
      answersParsed = {};
    }
  }
  
  const a = answersParsed || {};
  const empresa  = a.empresa  || a.company  || {};
  const titular  = a.titular  || a.representante || {};
  const contacto = a.contacto || a.contactoEmpresa || {};
  const cob      = a.cobranza || a.billing || a.contactoCobranza || {};

  // Direcciones
  const direccion = [app?.direccion, empresa?.direccion, empresa?.direccion_matriz, a?.direccion].find(Boolean) || '';
  const region    = [app?.region, empresa?.region, a?.region].find(Boolean) || '';
  const provincia = [app?.provincia, empresa?.provincia, a?.provincia].find(Boolean) || '';
  const comuna    = [app?.comuna, empresa?.comuna, a?.comuna].find(Boolean) || '';

  // Web y correos
  const web = [app?.paginaWeb, empresa?.pagina_web, empresa?.web, a?.web].find(Boolean) || '';
  const correoContacto = [app?.contactoCorreo, contacto?.correo, empresa?.correo, titular?.correo, a?.correo].find(Boolean) || '';

  // Teléfonos - buscar en múltiples ubicaciones posibles
  const telFijo = [
    // Buscar recursivamente en todo el objeto app
    findTelefonoFijo(app),
    // Campos directos de la entidad (si existen)
    app?.telefonoFijo,
    app?.telefono_fijo,
    // Desde answers.empresa
    empresa?.telefono_fijo,
    empresa?.telefonoFijo,
    empresa?.telefono,
    empresa?.telefono_fijo_empresa,
    empresa?.telefono_empresa,
    empresa?.telefono_fijo_contacto,
    // Desde answers.contacto
    contacto?.telefono_fijo,
    contacto?.telefonoFijo,
    contacto?.telefono,
    // Desde answers directamente
    a?.telefono_fijo,
    a?.telefonoFijo,
    a?.telefono,
    // Desde answers.titular (por si acaso)
    titular?.telefono_fijo,
    titular?.telefono,
  ].find(Boolean) || '';
  
  const telMovil = [
    app?.telefonoMovil,
    app?.contactoMovil,
    empresa?.telefono_movil,
    empresa?.telefonoMovil,
    contacto?.telefono,
    contacto?.telefono_movil,
    contacto?.movil,
    titular?.telefono,
    titular?.movil,
    a?.telefono_movil,
    a?.telefonoMovil,
    a?.movil,
  ].find(Boolean) || '';

  // ✅ PERIODICIDAD: Igual que región, provincia, comuna - buscar directamente desde el backend
  // El backend guarda en answers.empresa.periodicidad_pago el valor original del formulario ("Mensual", "Semestral", "Anual")
  // Si no está, usar el enum del backend (app.periodicidadPago) y transformarlo
  let periodicityRaw = [empresa?.periodicidad_pago, a?.periodicidad_pago].find(Boolean) || '';
  
  // Si no está en answers, usar el enum del backend y transformarlo
  if (!periodicityRaw && app?.periodicidadPago) {
    const pUpper = String(app.periodicidadPago).toUpperCase().trim();
    if (pUpper === 'MENSUAL') periodicityRaw = 'Mensual';
    else if (pUpper === 'SEMESTRAL') periodicityRaw = 'Semestral';
    else if (pUpper === 'ANNUAL' || pUpper === 'ANUAL') periodicityRaw = 'Anual';
  }
  
  // Asegurar que sea string y esté en el formato correcto
  const periodicity = periodicityRaw ? String(periodicityRaw).trim() : '';
  
  // ✅ TAMAÑO: Usar directamente desde el backend (ya guardado en MembershipDetailsModal)
  // app.tamanoEmpresa viene como enum: MICRO, SMALL, MEDIUM, LARGE
  // Necesitamos convertirlo al formato del formulario: Micro, Small, Medium, Big
  let companySize = '';
  if (app?.tamanoEmpresa) {
    const sizeUpper = String(app.tamanoEmpresa).toUpperCase();
    if (sizeUpper === 'MICRO') companySize = 'Micro';
    else if (sizeUpper === 'SMALL') companySize = 'Small';
    else if (sizeUpper === 'MEDIUM') companySize = 'Medium';
    else if (sizeUpper === 'LARGE' || sizeUpper === 'BIG') companySize = 'Big';
  }
  
  // Si no hay tamaño en el campo directo, buscar en answers o usar fallback
  if (!companySize) {
    const sizeFromAnswers = empresa?.tamano || a?.tamano;
    if (sizeFromAnswers) {
      const sizeUpper = String(sizeFromAnswers).toUpperCase();
      if (sizeUpper === 'MICRO') companySize = 'Micro';
      else if (sizeUpper === 'SMALL') companySize = 'Small';
      else if (sizeUpper === 'MEDIUM') companySize = 'Medium';
      else if (sizeUpper === 'LARGE' || sizeUpper === 'BIG') companySize = 'Big';
      else companySize = resolveCompanySize(app); // Fallback para formatos no estándar
    } else {
      companySize = resolveCompanySize(app); // Fallback completo
    }
  }
  
  // ✅ VALOR DE MEMBRESÍA: Usar directamente desde el backend (ya guardado en MembershipDetailsModal)
  // app.valorMembresia ya viene guardado cuando se edita en MembershipDetailsModal
  let monto = '';
  if (app?.valorMembresia) {
    // Limpiar el valor: extraer solo números y convertir coma a punto
    monto = String(app.valorMembresia).replace(/[^\d.,]/g, '').replace(',', '.');
  } else if (empresa?.valor_membresia) {
    // Fallback: buscar en answers si no está en el campo directo
    monto = String(empresa.valor_membresia).replace(/[^\d.,]/g, '').replace(',', '.');
  } else if (companySize) {
    // Si no hay valor guardado, calcular automáticamente según el tamaño
    monto = calculateMembershipValue(companySize);
  }

  // Contacto de cobranza - buscar en múltiples fuentes
  const cobNombre = [
    cob?.nombre,
    contacto?.nombre,
    titular?.nombre,
    empresa?.contacto_cobranza,
    a?.contacto_cobranza,
  ].find(Boolean) || '';
  
  const cobCorreo = [
    cob?.correo,
    contacto?.correo,
    titular?.correo,
    empresa?.correo_cobranza,
    a?.correo_cobranza,
  ].find(Boolean) || '';
  
  // Teléfono de cobranza - priorizar móvil si no hay teléfono específico de cobranza
  const cobTelefono = [
    cob?.telefono,
    cob?.telefono_movil,
    telMovil, // usar el teléfono móvil si no hay teléfono de cobranza
    contacto?.telefono,
    contacto?.telefono_movil,
    titular?.telefono,
    empresa?.telefono_cobranza,
    a?.telefono_cobranza,
  ].find(Boolean) || '';
  
  const cobCargo = [
    cob?.cargo,
    contacto?.cargo,
    titular?.cargo,
    empresa?.cargo_cobranza,
    a?.cargo_cobranza,
  ].find(Boolean) || '';
  
  const cobArea = [
    cob?.area,
    contacto?.area,
    empresa?.area_cobranza,
    a?.area_cobranza,
  ].find(Boolean) || '';

  return {
    rut: normalizeRut(app?.rutEmpresa || empresa?.rut || a?.rut),
    password: '', // se setea luego con passwordFromRut
    business_name: app?.razonSocial || empresa?.razon_social || '',
    trade_name: app?.nombreFantasia || empresa?.nombre_comercial || '',
    industry: app?.industria || empresa?.industria || '',
    role: 'socio',
    logo: null,

    status: 'Active', // ✅ Autocompletar con "Activo"
    company_size: companySize, // ← valor desde MembershipDetailsModal (Micro|Small|Medium|Big)
    entry_date: formatTodayDate(), // ✅ Autocompletar con fecha de hoy en formato "dd-mm-yyyy"
    description: '', // ✅ Dejar vacío

    contact_email: correoContacto,
    web_page: web,
    landline_phone: telFijo,
    mobile_phone: telMovil,
    address: direccion,
    region,
    province: provincia,
    district: comuna,

    amount: monto,
    periodicity, // ✅ Igual que región, provincia, comuna - valor directo desde MembershipDetailsModal

    // ✅ Contacto de cobranza - autocompletar todo desde múltiples fuentes
    contact_name: cobNombre,
    email: cobCorreo,
    phone_number: cobTelefono, // ✅ Usa teléfono móvil si no hay teléfono de cobranza
    title_job: cobCargo,
    area: cobArea,
  };
};

/** URL de logo desde la postulación */
const getLogoUrl = (app) => {
  const adj = app?.answers?.adjuntos || {};
  const cands = [
    get(adj, 'logo_alta.0.secureUrl'),
    get(adj, 'logo_alta.0.viewUrl'),
    get(adj, 'logo_alta.0.url'),
    get(adj, 'logo_alta.0.downloadUrl'),
    app?.coverImageUrl,
    app?.logoUrl,
  ].filter(Boolean);
  return cands[0] || null;
};

/** Descargar URL como File para validación de Formik */
const downloadAsFile = async (url, filename = 'logo_autocargado') => {
  const res = await fetch(url);
  const blob = await res.blob();
  const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/jpeg' ? 'jpg' : 'bin';
  return new File([blob], `${filename}.${ext}`, { type: blob.type || 'application/octet-stream' });
};

/* ---------- Tarjeta ---------- */
function AcceptedAppCard({ r, onPick, onOpenDetails, pickingId, used }) {
  const name = r.razonSocial || r.nombreFantasia || r.rutEmpresa || `#${r.id}`;
  const avatarUrl = getLogoUrl(r);
  const whenAccepted = r.docsGeneratedAt || r.updatedAt || r.createdAt;
  const region = r.region;
  const comuna = r.comuna;
  const contact = r.contactoCorreo || r.titularCorreo;
  const isPicking = pickingId === r.id;

  return (
    <div
      className={`group relative bg-white border rounded-xl p-4 shadow-sm transition-all
        ${used ? 'border-green-200 ring-1 ring-green-100 opacity-90' : 'border-gray-200 hover:shadow-md hover:border-indigo-200'}
      `}
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetails(r.id)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpenDetails(r.id)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center overflow-hidden shrink-0">
            {avatarUrl ? (
              <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-indigo-600 font-bold">{initials(name)}</span>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-indigo-700 truncate">{name}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <Building2 size={14} className="text-gray-400" />
              <span className="truncate">{r.actividadRubro || '—'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 space-y-1.5 text-[13px] text-gray-700">
        <div className="flex items-center gap-2">
          <IdCard size={16} className="text-gray-400" />
          <span className="truncate">{r.rutEmpresa || '—'}</span>
        </div>
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-gray-400" />
          <span className="truncate">
            {[region, comuna].filter(Boolean).join(' / ') || '—'}
          </span>
        </div>
        {contact && (
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-gray-400" />
            <span className="truncate">{contact}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <CalendarDays size={16} className="text-gray-400" />
          <span>Aceptada: {formatDate(whenAccepted) || '—'}</span>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-dashed border-gray-200 flex items-center justify-between">
        {/* Badge de estado */}
        <div>
          {used ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5">
              <CheckCircle size={14} className="text-green-600" />
              <span className="text-[11px] text-green-800 font-semibold uppercase">Completada</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5">
              <span className="w-2 h-2 rounded-full bg-emerald-600" />
              <span className="text-[11px] text-emerald-800 font-semibold uppercase">Aceptada</span>
            </span>
          )}
        </div>
        
        <button
          className={`px-3 py-1.5 rounded-lg text-white text-xs disabled:opacity-70
            ${used ? 'bg-green-600 cursor-default' : 'bg-indigo-600 hover:bg-indigo-700'}
          `}
          onClick={(e) => { e.stopPropagation(); if (!used) onPick(r.id); }}
          disabled={isPicking || used}
          title={used ? 'Ya completada' : 'Usar datos'}
        >
          {isPicking ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} /> Cargando…
            </span>
          ) : used ? (
            <span className="inline-flex items-center gap-2">
              <CheckCircle size={14} /> Completada
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 size={14} /> Usar datos
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

/* ---------- Modal principal ---------- */
export default function AcceptedAppsPickerModal({
  open,
  onClose,
  setFieldValue,
  setFieldTouched,
  setImagePreview,
  onPicked = () => {},        // ⬅️ avisa al padre qué app se usó
}) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [pickingId, setPickingId] = useState(null);
  const [error, setError] = useState('');
  const [hideUsed, setHideUsed] = useState(false);

  // "Completadas" - ahora se lee desde el backend (answers.autofill.used)
  // sessionStorage se mantiene como caché local pero no es la fuente de verdad
  const USED_KEY = 'acceptedApps:used';
  const [usedIds, setUsedIds] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(USED_KEY) || '[]');
      return new Set(saved);
    } catch { return new Set(); }
  });

  // Detalles
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsApp, setDetailsApp] = useState(null);
  const [detailsBusy, setDetailsBusy] = useState(false);

  const canShow = !!open;
  const debounceRef = useRef(null);

  // Buscar (debounce)
  useEffect(() => {
    if (!canShow) return;
    let alive = true;

    const run = async () => {
      try {
        setLoading(true);
        setError('');
        const j = await searchAcceptedMembershipApps({ q, page, pageSize: 12 });
        const data = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);
        if (alive) {
          setRows(data);
          setMeta(j?.meta || null);
          
          // ✅ Actualizar usedIds desde el backend (answers.autofill.used)
          // Esto asegura que los datos persistan incluso después de reiniciar el servidor
          const backendUsedIds = new Set();
          data.forEach((app) => {
            const autofill = app?.answers?.autofill;
            if (autofill?.used === true) {
              backendUsedIds.add(app.id);
            }
          });
          
          // Sincronizar con sessionStorage (caché local)
          if (backendUsedIds.size > 0) {
            const currentLocal = new Set(usedIds);
            backendUsedIds.forEach(id => currentLocal.add(id));
            const localArray = Array.from(currentLocal);
            try {
              sessionStorage.setItem(USED_KEY, JSON.stringify(localArray));
            } catch (e) {
              console.warn('No se pudo actualizar sessionStorage:', e);
            }
            setUsedIds(currentLocal);
          }
        }
      } catch (e) {
        console.error('searchAcceptedMembershipApps', e);
        if (alive) {
          setRows([]);
          setMeta(null);
          setError('No se pudo obtener la lista. Intenta nuevamente.');
        }
      } finally {
        alive && setLoading(false);
      }
    };

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(run, 350);

    return () => {
      alive = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [canShow, q, page]);

  // Autocompletar (usar datos) — NO marca completado aquí
  const handleUse = async (id) => {
    try {
      // Buscar la app para mostrar en el Swal
      const j = await getMembershipApplication(id);
      const app = j?.data || j;
      
      const name = app?.razonSocial || app?.nombreFantasia || app?.rutEmpresa || `ID ${id}`;
      const rut = app?.rutEmpresa || '—';
      
      // Mostrar confirmación
      const confirm = await Swal.fire({
        title: '¿Estás seguro de usar estos datos?',
        html: `<div class="text-left">
          <p class="mb-2"><strong>Empresa:</strong> ${name}</p>
          <p><strong>RUT:</strong> ${rut}</p>
        </div>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0488AF',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, usar estos datos',
        cancelButtonText: 'Cancelar',
      });

      if (!confirm.isConfirmed) {
        return; // Cancelar si el usuario no confirma
      }

      setPickingId(id);

      const mapped = mapAppToNewMember(app);
      const rutFuente = mapped.rut || app?.rutEmpresa || get(app, 'answers.empresa.rut', '');
      const passwd = passwordFromRut(rutFuente);

      // Setear campos en Formik
      Object.entries({ ...mapped, password: passwd }).forEach(([k, v]) => {
        setFieldValue(k, v ?? '');
        setFieldTouched(k, true, false);
      });

      // Guardar el ID fuente en el formulario (campo oculto)
      setFieldValue('sourceApplicationId', id);
      setFieldTouched('sourceApplicationId', true, false);

      // LOGO como File (si se puede)
      const logoUrl = getLogoUrl(app);
      if (logoUrl) {
        try {
          const file = await downloadAsFile(logoUrl, 'logo_autocargado');
          setFieldValue('logo', file);
          setFieldTouched('logo', true, false);
          setImagePreview(URL.createObjectURL(file));
        } catch (e) {
          console.warn('No se pudo descargar el logo:', e);
          setImagePreview(logoUrl); // al menos previsualiza
        }
      }

      // Avisar al padre (banner) - priorizar razón comercial (nombreFantasia)
      const razonComercial = app?.nombreFantasia || app?.razonSocial || app?.rutEmpresa || `ID ${id}`;
      onPicked({ id, name: razonComercial, razonSocial: app?.razonSocial, nombreFantasia: app?.nombreFantasia });

      // Cerrar el modal después de confirmar
      onClose();
    } catch (e) {
      console.error('use app for autofill', e);
      alert(e?.message || 'No se pudo autocompletar.');
    } finally {
      setPickingId(null);
    }
  };

  // Abrir detalles
  const openDetails = async (id) => {
    try {
      setDetailsBusy(true);
      const j = await getMembershipApplication(id);
      setDetailsApp(j?.data || j);
      setDetailsOpen(true);
    } catch (e) {
      console.error('open details', e);
      alert(e?.message || 'No se pudo abrir el detalle.');
    } finally {
      setDetailsBusy(false);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsApp(null);
  };

  // Lista filtrada si "ocultar completadas"
  // Verifica tanto usedIds (caché local) como answers.autofill.used (backend)
  const filteredRows = useMemo(() => {
    if (!hideUsed) return rows;
    return rows.filter(r => {
      // Priorizar datos del backend, luego caché local
      const isUsedInBackend = r?.answers?.autofill?.used === true;
      const isUsedInCache = usedIds.has(r.id);
      return !isUsedInBackend && !isUsedInCache;
    });
  }, [rows, hideUsed, usedIds]);

  if (!canShow) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-[96vw] max-w-5xl max-h-[88vh] overflow-hidden flex flex-col">
        {/* Banner RDS arriba */}
        <div className="relative rounded-t-2xl overflow-hidden">
          <Banner 
            title="Autocompletar desde postulación" 
            message="Selecciona una postulación aceptada para autocompletar los datos del nuevo socio. Los datos se copiarán automáticamente al formulario de registro."
            className="mb-0 rounded-t-2xl rounded-b-none"
          />
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/20 transition-colors text-white z-10" 
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search + opciones */}
        <div className="px-5 py-3 border-b">
          <div className="flex items-center gap-2 rounded-xl border px-3 py-2">
            <Search size={18} className="text-gray-500" />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Buscar por razón social, RUT, contacto…"
              className="flex-1 outline-none"
            />
            {loading && <Loader2 className="animate-spin text-gray-400" size={18} />}
          </div>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Se listan solo postulaciones que han sido aceptadas.
            </p>
            <label className="text-xs text-gray-700 inline-flex items-center gap-2 select-none">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={hideUsed}
                onChange={(e) => setHideUsed(e.target.checked)}
              />
              Ocultar completadas
            </label>
          </div>
          {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1" style={{ maxHeight: 'calc(88vh - 200px)' }}>
          {loading && rows.length === 0 ? (
            <div className="p-16 text-center text-gray-500">
              <Loader2 className="animate-spin mx-auto mb-2" /> Cargando…
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-16 text-center text-gray-500">Sin resultados</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRows.map((r) => (
                <AcceptedAppCard
                  key={r.id}
                  r={r}
                  onPick={handleUse}
                  onOpenDetails={openDetails}
                  pickingId={pickingId}
                  used={r?.answers?.autofill?.used === true || usedIds.has(r.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer / Paginación */}
        <div className="px-5 py-4 border-t flex items-center justify-between">
          <button className="px-3 py-2 text-sm rounded hover:bg-gray-100" onClick={onClose}>
            Cerrar
          </button>

          <div className="flex items-center gap-3 text-sm">
            <span className="text-gray-600">
              {meta?.total ? `Resultados: ${meta.total}` : ''}
            </span>
            <button
              className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Anterior
            </button>
            <span>Página {page}{meta?.totalPages ? ` de ${meta.totalPages}` : ''}</span>
            <button
              className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
              onClick={() => setPage((p) => p + 1)}
              disabled={filteredRows.length === 0 || (meta && page >= (meta.totalPages || 9999))}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Detalles */}
      <DetailsModal
        app={detailsApp}
        open={detailsOpen}
        onClose={closeDetails}
        onChanged={() => {}}
        onBusy={() => {}}
        allowEditWhenAccepted={true}
      />
    </div>
  );
}
